import React, { useCallback, useEffect, useState } from "react";
import {
  Search, ClipboardCheck, CheckCircle2, AlertTriangle, XCircle,
  ExternalLink, Star, MessageSquare, Globe, Phone, Clock,
  Image, FileText, Tag, Info, Trash2, UserPlus, ChevronDown, ChevronUp,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiError, arr } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";

// ─── helpers ──────────────────────────────────────────────────────────────

const STATUS_ICON = {
  good: <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />,
  weak: <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />,
  missing: <XCircle className="w-4 h-4 text-red-400 shrink-0" />,
};
const STATUS_BG = { good: "bg-emerald-50 border-emerald-200", weak: "bg-amber-50 border-amber-200", missing: "bg-red-50 border-red-200" };
const STATUS_TEXT = { good: "text-emerald-700", weak: "text-amber-700", missing: "text-red-600" };

const CHECK_ICONS = {
  rating: Star, reviewCount: MessageSquare, website: Globe, phone: Phone,
  hours: Clock, photos: Image, description: FileText, category: Tag,
  recentPosts: RefreshCw, qa: Info, reviewReplies: MessageSquare,
};

function ScoreRing({ score }) {
  const r = 36, circ = 2 * Math.PI * r;
  const color = score >= 70 ? "#10b981" : score >= 45 ? "#f59e0b" : "#ef4444";
  return (
    <svg viewBox="0 0 88 88" className="w-24 h-24">
      <circle cx="44" cy="44" r={r} fill="none" stroke="#e2e8f0" strokeWidth="8" />
      <circle
        cx="44" cy="44" r={r} fill="none" stroke={color} strokeWidth="8"
        strokeDasharray={circ} strokeDashoffset={circ * (1 - score / 100)}
        strokeLinecap="round" transform="rotate(-90 44 44)"
        style={{ transition: "stroke-dashoffset 0.8s ease" }}
      />
      <text x="44" y="44" textAnchor="middle" dominantBaseline="central"
        fontSize="18" fontWeight="700" fill={color}>{score}</text>
      <text x="44" y="58" textAnchor="middle" dominantBaseline="central"
        fontSize="9" fill="#94a3b8">/ 100</text>
    </svg>
  );
}

function CheckRow({ c }) {
  const Icon = CHECK_ICONS[c.key] || Tag;
  return (
    <div className={`flex items-start gap-3 rounded-xl border p-3 ${STATUS_BG[c.status]}`}>
      {STATUS_ICON[c.status]}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          <span className="text-sm font-medium text-slate-700">{c.label}</span>
          {c.estimated && (
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
              <Info className="w-3 h-3" /> Estimated
            </span>
          )}
        </div>
        <p className={`mt-0.5 text-xs ${STATUS_TEXT[c.status]} truncate`}>{c.value}</p>
        {c.tip && <p className="mt-1 text-xs text-slate-500 leading-snug">{c.tip}</p>}
      </div>
    </div>
  );
}

function ConvertModal({ audit, onClose, onConverted }) {
  const [form, setForm] = useState({
    ownerName: "", email: "", username: "", password: "", plan: "Starter",
  });
  const [saving, setSaving] = useState(false);

  const up = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.email || !form.username || !form.password)
      return toast.error("Email, username, and password are required");
    setSaving(true);
    try {
      const { data } = await api.post(`/prospect-audit/${audit._id}/convert`, form);
      toast.success(`Client "${data.client.businessName}" created`);
      onConverted(data.client);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden">
        <div className="bg-brand-700 px-6 py-4">
          <h2 className="text-lg font-semibold text-white">Convert to Client</h2>
          <p className="text-sm text-brand-200 mt-0.5">{audit.businessName}</p>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="form-label">Owner Name</label>
            <input className="form-input" value={form.ownerName}
              onChange={e => up("ownerName", e.target.value)} placeholder="Jane Smith" />
          </div>
          <div>
            <label className="form-label">Login Email *</label>
            <input className="form-input" type="email" value={form.email}
              onChange={e => up("email", e.target.value)} placeholder="jane@business.com" />
          </div>
          <div>
            <label className="form-label">Username *</label>
            <input className="form-input" value={form.username}
              onChange={e => up("username", e.target.value.toLowerCase().replace(/\s/g, ""))}
              placeholder="janebusiness" />
          </div>
          <div>
            <label className="form-label">Temporary Password *</label>
            <input className="form-input" type="password" value={form.password}
              onChange={e => up("password", e.target.value)} placeholder="Min 8 characters" />
          </div>
          <div>
            <label className="form-label">Plan</label>
            <select className="form-input" value={form.plan} onChange={e => up("plan", e.target.value)}>
              {["Starter", "Business", "Pro", "Agency"].map(p => (
                <option key={p}>{p}</option>
              ))}
            </select>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed">
            Pre-filled: business name, phone, review link, and category from the audit.
            The client will be asked to connect their Google account on first login.
          </p>
        </div>
        <div className="flex gap-3 px-6 pb-6">
          <button onClick={onClose} className="btn-outline flex-1">Cancel</button>
          <button onClick={submit} disabled={saving} className="btn-primary flex-1">
            {saving ? "Creating…" : "Create Client Account"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AuditCard({ audit, onDelete, onReopen }) {
  const [open, setOpen] = useState(false);
  const scoreColor = audit.healthScore >= 70 ? "text-emerald-600" : audit.healthScore >= 45 ? "text-amber-600" : "text-red-500";

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <button
        className="w-full flex items-center gap-4 px-5 py-4 hover:bg-slate-50 text-left transition-colors"
        onClick={() => setOpen(o => !o)}
      >
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-800 truncate">{audit.businessName || "Unknown Business"}</p>
          <p className="text-xs text-slate-400 mt-0.5 truncate">{audit.address}</p>
        </div>
        <span className={`text-lg font-bold tabular-nums ${scoreColor}`}>{audit.healthScore}%</span>
        {audit.convertedClientId && (
          <span className="hidden sm:inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 text-xs text-emerald-700 font-medium">
            <CheckCircle2 className="w-3 h-3" /> Converted
          </span>
        )}
        {open ? <ChevronUp className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />}
      </button>

      {open && (
        <div className="border-t border-slate-100 px-5 py-4 space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            {audit.rating != null && <div className="rounded-xl bg-slate-50 p-3 text-center"><p className="text-xs text-slate-400">Rating</p><p className="font-bold text-slate-700">{audit.rating} ★</p></div>}
            <div className="rounded-xl bg-slate-50 p-3 text-center"><p className="text-xs text-slate-400">Reviews</p><p className="font-bold text-slate-700">{audit.reviewCount}</p></div>
            <div className="rounded-xl bg-slate-50 p-3 text-center"><p className="text-xs text-slate-400">Photos</p><p className="font-bold text-slate-700">{audit.photoCount}</p></div>
            <div className="rounded-xl bg-slate-50 p-3 text-center"><p className="text-xs text-slate-400">Score</p><p className={`font-bold ${scoreColor}`}>{audit.healthScore}%</p></div>
          </div>

          <div className="grid sm:grid-cols-2 gap-2">
            {(audit.checks || []).map(c => <CheckRow key={c.key} c={c} />)}
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            {audit.mapsUrl && (
              <a href={audit.mapsUrl} target="_blank" rel="noreferrer" className="btn-outline text-xs gap-1.5">
                <ExternalLink className="w-3.5 h-3.5" /> Open in Maps
              </a>
            )}
            {!audit.convertedClientId && (
              <button onClick={() => onReopen(audit)} className="btn-primary text-xs gap-1.5">
                <UserPlus className="w-3.5 h-3.5" /> Convert to Client
              </button>
            )}
            <button onClick={() => onDelete(audit._id)}
              className="ml-auto inline-flex items-center gap-1.5 rounded-xl border border-red-100 bg-red-50 px-3 py-1.5 text-xs text-red-600 hover:bg-red-100 transition-colors">
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────

export default function ProspectAuditPage() {
  const [url, setUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);       // latest audit result shown at top
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [convertTarget, setConvertTarget] = useState(null);
  const [showNotes, setShowNotes] = useState(false);

  const loadAudits = useCallback(async () => {
    try {
      const { data } = await api.get("/prospect-audit");
      setAudits(arr(data));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAudits(); }, [loadAudits]);

  const analyze = async () => {
    if (!url.trim()) return toast.error("Paste a Google Maps or GBP URL first");
    setAnalyzing(true);
    setResult(null);
    try {
      const { data } = await api.post("/prospect-audit/analyze", { url: url.trim(), notes });
      setResult(data);
      setAudits(prev => [data, ...prev.filter(a => a._id !== data._id)]);
      setUrl("");
      setNotes("");
      toast.success(`Audit complete — Health Score: ${data.healthScore}%`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setAnalyzing(false);
    }
  };

  const deleteAudit = async (id) => {
    if (!window.confirm("Delete this audit?")) return;
    try {
      await api.delete(`/prospect-audit/${id}`);
      setAudits(prev => prev.filter(a => a._id !== id));
      if (result?._id === id) setResult(null);
      toast.success("Deleted");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const handleConverted = (client) => {
    setConvertTarget(null);
    setAudits(prev => prev.map(a =>
      a._id === convertTarget._id ? { ...a, convertedClientId: client._id } : a
    ));
    if (result?._id === convertTarget._id) {
      setResult(prev => ({ ...prev, convertedClientId: client._id }));
    }
  };

  const scoreColor = result
    ? result.healthScore >= 70 ? "text-emerald-600" : result.healthScore >= 45 ? "text-amber-600" : "text-red-500"
    : "";

  return (
    <div className="space-y-6">
      <PageHeader
        title="Prospect Audit"
        subtitle="Audit any public Google Business Profile, score it, and convert prospects to clients"
        icon={ClipboardCheck}
      />

      {/* ── URL Input ── */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm space-y-4">
        <h2 className="text-sm font-semibold text-slate-700">Audit New Prospect</h2>
        <div className="flex gap-3">
          <input
            className="form-input flex-1"
            placeholder="Paste Google Maps URL — e.g. https://maps.google.com/maps/place/Business+Name/..."
            value={url}
            onChange={e => setUrl(e.target.value)}
            onKeyDown={e => e.key === "Enter" && analyze()}
          />
          <button onClick={analyze} disabled={analyzing} className="btn-primary shrink-0 gap-2 px-5">
            {analyzing ? (
              <><RefreshCw className="w-4 h-4 animate-spin" /> Analyzing…</>
            ) : (
              <><Search className="w-4 h-4" /> Audit</>
            )}
          </button>
        </div>

        <button
          onClick={() => setShowNotes(n => !n)}
          className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
        >
          {showNotes ? "Hide" : "+ Add"} internal notes
        </button>
        {showNotes && (
          <textarea
            className="form-input h-16 py-2"
            placeholder="Internal notes about this prospect (won't be shown to client)…"
            value={notes}
            onChange={e => setNotes(e.target.value)}
          />
        )}

        <p className="text-xs text-slate-400 leading-relaxed">
          Uses the <strong>Google Places API</strong> (public data only). Fields marked
          <span className="mx-1 inline-flex items-center gap-0.5 rounded-full bg-slate-100 px-1.5 text-slate-500 font-medium">
            <Info className="w-3 h-3" /> Estimated
          </span>
          require Google OAuth to confirm — the prospect must connect their account for those to be actionable.
        </p>
      </div>

      {/* ── Audit Result ── */}
      {result && (
        <div className="rounded-2xl border border-brand-200 bg-white shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-brand-700 to-brand-600 px-6 py-5 flex items-center gap-5">
            <ScoreRing score={result.healthScore} />
            <div className="flex-1 min-w-0">
              <p className="text-lg font-bold text-white leading-snug">{result.businessName}</p>
              <p className="text-sm text-brand-200 mt-0.5 truncate">{result.address}</p>
              <div className="mt-2 flex flex-wrap gap-3 text-sm text-white/80">
                {result.rating != null && <span>⭐ {result.rating} ({result.reviewCount} reviews)</span>}
                {result.phone && <span>📞 {result.phone}</span>}
                {result.website && <span>🌐 Has website</span>}
              </div>
            </div>
            {result.mapsUrl && (
              <a href={result.mapsUrl} target="_blank" rel="noreferrer"
                className="shrink-0 inline-flex items-center gap-1.5 rounded-xl bg-white/20 hover:bg-white/30 px-3 py-1.5 text-sm text-white transition-colors">
                <ExternalLink className="w-3.5 h-3.5" /> Maps
              </a>
            )}
          </div>

          <div className="p-6 space-y-4">
            <div className="grid grid-cols-3 gap-2 text-center text-xs text-slate-500">
              {[
                { label: "Good", count: arr(result.checks).filter(c => c.status === "good").length, color: "text-emerald-600" },
                { label: "Weak", count: arr(result.checks).filter(c => c.status === "weak").length, color: "text-amber-600" },
                { label: "Missing", count: arr(result.checks).filter(c => c.status === "missing").length, color: "text-red-500" },
              ].map(({ label, count, color }) => (
                <div key={label} className="rounded-xl bg-slate-50 py-2">
                  <p className={`text-xl font-bold ${color}`}>{count}</p>
                  <p>{label}</p>
                </div>
              ))}
            </div>

            <div className="grid sm:grid-cols-2 gap-2">
              {arr(result.checks).map(c => <CheckRow key={c.key} c={c} />)}
            </div>

            <div className="flex gap-3 pt-2">
              {!result.convertedClientId ? (
                <button onClick={() => setConvertTarget(result)} className="btn-primary gap-2">
                  <UserPlus className="w-4 h-4" /> Convert to Client
                </button>
              ) : (
                <span className="inline-flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-2 text-sm text-emerald-700 font-medium">
                  <CheckCircle2 className="w-4 h-4" /> Converted to Client
                </span>
              )}
              {result.mapsUrl && (
                <a href={result.mapsUrl} target="_blank" rel="noreferrer" className="btn-outline gap-2">
                  <ExternalLink className="w-4 h-4" /> Open in Maps
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Past Audits ── */}
      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-slate-600 px-1">Past Audits ({audits.length})</h2>
        {loading ? (
          <div className="text-center py-10 text-slate-400 text-sm">Loading…</div>
        ) : audits.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 py-12 text-center">
            <ClipboardCheck className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-400">No audits yet. Paste a Google Maps URL above to run your first one.</p>
          </div>
        ) : (
          audits.map(a => (
            <AuditCard
              key={a._id}
              audit={a}
              onDelete={deleteAudit}
              onReopen={setConvertTarget}
            />
          ))
        )}
      </div>

      {convertTarget && (
        <ConvertModal
          audit={convertTarget}
          onClose={() => setConvertTarget(null)}
          onConverted={handleConverted}
        />
      )}
    </div>
  );
}
